import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PADSideNavbar from './components/PADSideNavbar';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { ChevronRightIcon, ChevronLeftIcon, PlusIcon, CheckIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';
import { QuestionMarkCircleIcon, CheckCircleIcon } from '@heroicons/react/solid';
import SelectionOptions from './components/SelectionOptions';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import {
  getPadStageOrder,
  getPadSidebarIndex,
  getPadSidebarSections,
  getPadNavLinks,
  rewriteSubsectionTitleDisplay,
  HEALTH_CHECKS_GATE_PATH,
  buildPadSidebarUnlockFlags,
  resolvePatientTreatmentPreferencesAnswer,
  isPatientTreatmentPreferencesComplete,
  getPatientTreatmentPreferencesProceedBlockReason,
} from '../../../utils/padNavigationUtils';
import {
  buildClearDeferredHealthChecksReportUpdate,
  isHealthChecksGateDeferred,
} from '../../../utils/padHealthChecksReportUtils';

export default function PADTreatmentOptions() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  // When the AMP stage is disabled for this org (e.g. Well Life Clinic), the
  // Agreed Medication Plan (section 5) is hidden from the PAD sidebar.
  const { isAgreedMedicationPlanEnabled, isPadTreatmentFirst = false, isAppointmentDatesEnabled = false } = useWorkflowConfig(patientDataModel, claims);
  const [hasChanges, setHasChanges] = useState(false);

  // Determines whether this user is the clinician assigned to the PAD stage.
  // When false the entire page renders in read-only mode.
  const canEdit = useCanEditStage(patientDataModel, 'PAD');

  // Registers dirty state with ProformaNavigationGuardContext and provides
  // unmount-based regeneration as a safety net for unmanaged exits (breadcrumb,
  // top nav, browser back). markRegenHandled() is called in handleSectionCompletion
  // to prevent double-firing when the managed Next/Finish/Sidebar path is used.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'Post-Assessment Discussion',
    sectionTitle: 'Treatment Options',
    hasChanges,
    canEdit,
    patientDataModel,
  });

  const [formState, setFormState] = useState({});

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Add these handler functions near your other handler functions
  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error completing section:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateReport = () => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/documents`);
  };

  // Constants
  const currentStageTitle = 'Post-Assessment Discussion';
  const currentSectionNumber = 3;
  const currentSectionTitle = 'Treatment Options';

  // Ordered stage list for the clinician PAD — controls sidebar order, display
  // numbers, and prev/next links. Treatment-first (SLaM) swaps sections 2/3.
  const padStageOrder = useMemo(
    () => getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }),
    [isPadTreatmentFirst, isAgreedMedicationPlanEnabled]
  );

  // Get the section data using useMemo
  const sectionData = useMemo(() => {
    return patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber] || {};
  }, [patientDataModel]);

  // Get subsections data
  const subsections = useMemo(() => {
    return sectionData?.subsections || {};
  }, [sectionData]);

  // MDT Plan selections — used to show "Added to plan in MDT" badge on items
  // the clinician flagged during the Diagnostic Decision.
  const mdtPlanTreatments = useMemo(() => (
    patientDataModel?.dataModel?.['4_diagnosticDecision']?.mdtPlan?.toDiscussTreatments || {}
  ), [patientDataModel]);

  // Strategy-level MDT Plan toggles — "Non-Pharmacological Options" is added
  // to the plan as a strategy (not per-item treatment), so we badge it at the
  // subsection panel level rather than per line item.
  const mdtPlanStrategies = useMemo(() => (
    patientDataModel?.dataModel?.['4_diagnosticDecision']?.mdtPlan?.toDiscussStrategies || {}
  ), [patientDataModel]);

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  // Update the checkConditionsToShow function to accept an optional state parameter
const checkConditionsToShow = useCallback((conditions, stateToCheck = null) => {
  if (!conditions) return true;
  
  // Use provided state or default to formState
  const state = stateToCheck || formState;

  return Object.values(conditions).every(condition => {
    const fieldValue = state[condition.firestoreFieldName]?.value || [];

    // Check if value matches any required values
    if (condition.valueIsOneOf) {
      const hasMatchingValue = fieldValue.some(value =>
        condition.valueIsOneOf.includes(value)
      );
      if (!hasMatchingValue) return false;
    }

    // Check if value doesn't match any excluded values
    if (condition.valueIsNotOneOf) {
      const hasExcludedValue = fieldValue.some(value =>
        condition.valueIsNotOneOf.includes(value)
      );
      if (hasExcludedValue) return false;
    }

    return true;
  });
}, [formState]);

  // Common function to update Firestore
  const updateFirestore = useCallback((newUpdates) => {
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
    setHasChanges(true);
  }, [patientId, updatePatientFirestore, currentSectionNumber]);

  // Initialize form state from Firestore data
  useEffect(() => {
    // Only run on initial load
    if (!isInitialLoad.current) return;
    if (!sectionData?.subsections) return;

    const initialFormState = {};

    Object.entries(sectionData.subsections).forEach(([subsectionId, subsection]) => {
      // Initialize form state for questions
      if (subsection?.questions) {
        Object.entries(subsection.questions).forEach(([questionId, question]) => {
          if (question?.firestoreFieldName) {
            initialFormState[question.firestoreFieldName] = {
              value: question?.answer?.value || [],
              otherValue: question?.answer?.otherValue || ''
            };
          }
        });
      }
    });

    setFormState(initialFormState);

    // Mark initial load as complete
    isInitialLoad.current = false;
  }, [sectionData]);

  // Function to handle horizontal selection changes
  const handleHorizontalSelect = async (subsectionId, fieldName, option, isSelected) => {
    if (!canEdit) return;
    try {
      // Get current selections
      const currentSelections = formState[fieldName]?.value || [];
      let newSelections;

      if (isSelected) {
        // Remove the option if it's already selected
        newSelections = currentSelections.filter(opt => opt !== option);
      } else {
        // Add the new option
        newSelections = [...currentSelections, option];
      }

      // Create update object
      const updateObject = {
        value: newSelections,
        otherValue: formState[fieldName]?.otherValue || ''
      };

      // Update local state first
      setFormState(prev => ({
        ...prev,
        [fieldName]: updateObject
      }));

      // Find the question index in the subsection
      const questionIndex = Object.entries(sectionData.subsections[subsectionId].questions)
        .find(([_, q]) => q.firestoreFieldName === fieldName)?.[0];

      if (!questionIndex) {
        throw new Error('Question not found');
      }

      // Uses nested-object format (matching CI/SSI convention) so the write
      // can be deep-merged with other section writes when batched together.
      await updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [currentSectionNumber]: {
              subsections: {
                [subsectionId]: {
                  questions: {
                    [questionIndex]: { answer: updateObject }
                  }
                }
              }
            }
          }
        }
      });
      setHasChanges(true);
    } catch (error) {
      console.error('Error updating Firestore:', error);
      // Revert local state if Firestore update fails
      setFormState(prev => ({
        ...prev,
        [fieldName]: {
          value: formState[fieldName]?.value || [],
          otherValue: formState[fieldName]?.otherValue || ''
        }
      }));
    }
  };
  
  // Function to handle vertical selection changes
const handleSelect = async (fieldName, selectedOption, isSelected, question, isNoneOption = false) => {
  if (!canEdit) return;
  try {
    // Get current selections
    const currentSelections = formState[fieldName]?.value || [];
    let newSelections;

    if (question.questionType === 'selectOne') {
      // For selectOne, only allow one option at a time
      newSelections = isSelected ? [] : [selectedOption];
    } else {
      // For selectMultiple
      if (isNoneOption) {
        // When selecting a none option, clear all other selections
        newSelections = isSelected ? [] : [selectedOption];
      } else {
        if (isSelected) {
          // Remove the option if it's already selected
          newSelections = currentSelections.filter(option => option !== selectedOption);
        } else {
          // Add the new option and remove any none options
          newSelections = [...currentSelections.filter(opt =>
            !question.noneOptions?.includes(opt)
          ), selectedOption];
        }
      }
    }

    // Check if this field is a parent field that affects other fields' visibility
    const dependentFields = [];
    
    // Identify any dependent fields by checking all questions in all subsections
    Object.entries(subsections).forEach(([subsectionId, subsection]) => {
      Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
        // Check if this question has conditions that depend on the field we're changing
        if (q.conditionsToShow && Object.values(q.conditionsToShow).some(
          condition => condition.firestoreFieldName === fieldName
        )) {
          dependentFields.push({
            fieldName: q.firestoreFieldName,
            subsectionId,
            questionId
          });
        }
      });
    });

    // Create update object
    const updateObject = {
      value: newSelections,
      otherValue: formState[fieldName]?.otherValue || ''
    };

    // Update local state first
    setFormState(prev => ({
      ...prev,
      [fieldName]: updateObject
    }));

    // Find the subsectionId and questionId for this field
    let targetSubsectionId, targetQuestionId;

    // Loop through subsections to find the question with matching firestoreFieldName
    Object.entries(subsections).forEach(([subsectionId, subsection]) => {
      Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
        if (q.firestoreFieldName === fieldName) {
          targetSubsectionId = subsectionId;
          targetQuestionId = questionId;
        }
      });
    });

    if (!targetSubsectionId || !targetQuestionId) {
      throw new Error(`Question not found for field: ${fieldName}`);
    }

    // Build a single nested-object write covering the primary answer and
    // any dependent field resets. Nested format (not dot-notation) prevents
    // conflicting ancestor/descendant field paths in Firestore's update()
    // when this write is batched with other section writes.
    const subsectionsPayload = {
      [targetSubsectionId]: {
        questions: {
          [targetQuestionId]: { answer: updateObject }
        }
      }
    };

    // Process dependent fields that may need to be reset
    const updatedFormState = { ...formState, [fieldName]: updateObject };
    for (const depField of dependentFields) {
      const depQuestion = subsections[depField.subsectionId]?.questions?.[depField.questionId];
      
      const shouldShowDep = checkConditionsToShow(depQuestion.conditionsToShow, updatedFormState);
      
      if (!shouldShowDep) {
        const resetValue = {
          value: [],
          otherValue: ''
        };

        if (!subsectionsPayload[depField.subsectionId]) {
          subsectionsPayload[depField.subsectionId] = { questions: {} };
        }
        subsectionsPayload[depField.subsectionId].questions[depField.questionId] = { answer: resetValue };
        
        setFormState(prev => ({
          ...prev,
          [depField.fieldName]: resetValue
        }));
      }
    }

    // If the clinician just left Yes on the health-checks gate, wipe any
    // Health Checks / AMP paragraphs from an earlier Yes visit so they cannot
    // linger in RD_PAD / the CAR after a No - defer.
    const clearReportUpdate =
      fieldName === HEALTH_CHECKS_GATE_PATH.firestoreFieldName &&
      isHealthChecksGateDeferred(newSelections)
        ? buildClearDeferredHealthChecksReportUpdate(patientDataModel)
        : null;

    await updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: {
            subsections: subsectionsPayload
          }
        }
      },
      ...(clearReportUpdate || {}),
    });
    setHasChanges(true);
  } catch (error) {
    console.error('Error updating Firestore:', error);
    // Revert local state if Firestore update fails
    setFormState(prev => ({
      ...prev,
      [fieldName]: {
        value: formState[fieldName]?.value || [],
        otherValue: formState[fieldName]?.otherValue || ''
      }
    }));
  }
};

  // Function to handle changes in the "Other" text field
const handleOtherTextChange = (fieldName, text) => {
  if (!canEdit) return;
  const updateObject = {
    value: formState[fieldName]?.value || [],
    otherValue: text || ''  // Default to empty string
  };

  setFormState(prevState => ({
    ...prevState,
    [fieldName]: updateObject
  }));
  setHasChanges(true);
};

// Function to handle blur event on the "Other" text field.
// Uses nested-object format (not dot-notation) so the write can be
// deep-merged with any sibling writes (e.g. status updates) when both
// land in the same queue batch. Dot-notation would create conflicting
// ancestor/descendant field paths in Firestore's update().
const handleOtherTextBlur = (fieldName) => {
  if (!canEdit) return;
  const currentValue = formState[fieldName];
  const updateObject = {
    value: currentValue?.value || [],
    otherValue: currentValue?.otherValue || ''
  };

  let targetSubsectionId, targetQuestionId;

  Object.entries(subsections).forEach(([subsectionId, subsection]) => {
    Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
      if (q.firestoreFieldName === fieldName) {
        targetSubsectionId = subsectionId;
        targetQuestionId = questionId;
      }
    });
  });

  if (!targetSubsectionId || !targetQuestionId) {
    console.error(`Question not found for field: ${fieldName}`);
    return;
  }

  updatePatientFirestore(patientId, {
    '5_postAssessmentDiscussion': {
      sections: {
        [currentSectionNumber]: {
          subsections: {
            [targetSubsectionId]: {
              questions: {
                [targetQuestionId]: {
                  answer: updateObject
                }
              }
            }
          }
        }
      }
    }
  });
  setHasChanges(true);
};

  // Optimistic compute: derive immediately from local formState, fallback to server state
  const isCarryOutHealthChecksNow = useMemo(() => {
    const carryOutHealthChecksQuestion = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['3']?.subsections?.['3']?.questions?.['2'];
    const fieldName = carryOutHealthChecksQuestion?.firestoreFieldName;
    const localSelected = fieldName ? (formState[fieldName]?.value || []).includes('Yes') : undefined;
    if (typeof localSelected === 'boolean') return localSelected;
    return carryOutHealthChecksQuestion?.answer?.value?.includes('Yes') || false;
  }, [patientDataModel, formState]);

  // Navigation links derived from the ordered stage list. For Treatment Options
  // (key '3'), the gate behaviour adapts: in strategies-first, TRT owns the
  // health-checks gate (Finish vs. advance to HLT). In treatment-first, TRT
  // always advances to Strategies because the gate is relocated to DIS.
  const padNav = useMemo(
    () => getPadNavLinks('3', padStageOrder, { isCarryOutHealthChecksNow }),
    [padStageOrder, isCarryOutHealthChecksNow]
  );
  const prevSectionLink = padNav.prevLink || '/proforma/post-assessment-discussion/strategies-and-recommendations';
  const prevSectionTitle = padNav.prevTitle || 'Strategies and Recommendations';
  const nextSectionLink = padNav.nextLink || '/proforma/post-assessment-discussion/health-checks';
  const nextSectionTitle = padNav.nextTitle || 'Health Checks';

  const SECTIONS_PAD = useMemo(() => {
    const isDiagnosisADHD = isPadAdhdOutcomePositive(patientDataModel);
    const isBeginningMedicationNow = isCarryOutHealthChecksNow && patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['4']?.subsections?.['4']?.questions?.['2']?.answer?.value?.includes("Yes");

    return getPadSidebarSections(
      patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections,
      padStageOrder,
      buildPadSidebarUnlockFlags({
        patientDataModel,
        isPadTreatmentFirst,
        isDiagnosisADHD,
        isCarryOutHealthChecksNow,
        isBeginningMedicationNow,
        formState,
      }),
    );
  }, [patientDataModel, isCarryOutHealthChecksNow, padStageOrder, isPadTreatmentFirst, formState]);

  // Treatment-first: block Next → Strategies until 3.3.Q1 is valid so the
  // health-checks gate on DIS is deterministic when the clinician arrives there.
  const treatmentPreferencesQuestion = subsections?.['3']?.questions?.['1'];
  const canProceedToStrategies = useMemo(() => {
    if (!isPadTreatmentFirst) return true;
    return isPatientTreatmentPreferencesComplete(
      resolvePatientTreatmentPreferencesAnswer(formState, patientDataModel),
      treatmentPreferencesQuestion,
    );
  }, [isPadTreatmentFirst, formState, patientDataModel, treatmentPreferencesQuestion]);

  const isNextToStrategiesDisabled = isNavigating || !canProceedToStrategies;
  const treatmentPrefsBlockReason = useMemo(() => {
    if (!isPadTreatmentFirst || canProceedToStrategies) return null;
    return getPatientTreatmentPreferencesProceedBlockReason(
      resolvePatientTreatmentPreferencesAnswer(formState, patientDataModel),
      treatmentPreferencesQuestion,
    );
  }, [
    isPadTreatmentFirst,
    canProceedToStrategies,
    formState,
    patientDataModel,
    treatmentPreferencesQuestion,
  ]);
  const showTreatmentPrefsGateTooltip =
    isPadTreatmentFirst && canEdit && Boolean(treatmentPrefsBlockReason);

  // Handle section completion — applies conditional guards to avoid redundant
  // Firestore writes and LLM generation calls on revisits where nothing changed.
  const handleSectionCompletion = useCallback(() => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    if (needsStatusWrite) {
      updateFirestore({ status: 'completed', progress: 100 });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [canEdit, patientDataModel, currentSectionNumber, updateFirestore, patientId, currentStageTitle, currentSectionTitle, hasChanges, flushPendingUpdates, markRegenHandled]);

  const handleNextSection = useCallback(() => {
    if (isNavigating || !canProceedToStrategies) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${nextSectionLink}`);
    });
  }, [isNavigating, canProceedToStrategies, patientId, navigate, nextSectionLink, handleSectionCompletion]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, handleSectionCompletion]);

  const handleBackNavigation = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${prevSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, prevSectionLink, handleSectionCompletion]);

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <PADSideNavbar
          sections={SECTIONS_PAD}
          currentSectionIndex={getPadSidebarIndex(padStageOrder, '3')}
          navigateToSection={navigateToSection}
          isNavigating={isSaving || isNavigating}
        />
        <div className="flex-grow p-8">

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleBackNavigation}
              disabled={isSaving || isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              aria-label="Back to Previous Section"
            >
              {(isSaving || isNavigating)
                ? <ButtonSpinner className="w-5 h-5 mr-2" />
                : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
              <span className="font-bold">{(isSaving || isNavigating) ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
            </button>

            {/* Show Next when the helper provides a nextLink (health checks Yes
                in strategies-first, or always in treatment-first); otherwise
                show Finish when the helper says showFinish (gate declined). */}
            {!padNav.showFinish ? (
              <div>
                <button
                  onClick={handleNextSection}
                  disabled={isNextToStrategiesDisabled}
                  data-tooltip-id="treatment-prefs-tooltip-top"
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ease-in-out ml-auto ${
                    isNextToStrategiesDisabled
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
                >
                  <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                  {isNavigating
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
                {showTreatmentPrefsGateTooltip && (
                  <Tooltip
                    id="treatment-prefs-tooltip-top"
                    place="top"
                    className="z-50 text-xs font-semibold"
                    style={{
                      maxWidth: '250px',
                      whiteSpace: 'normal',
                      backgroundColor: 'rgba(34, 34, 34, 1)',
                      borderRadius: '15px',
                      color: '#fff',
                    }}
                  >
                    {treatmentPrefsBlockReason}
                  </Tooltip>
                )}
              </div>
            ) : (
              canEdit && (
                <button
                  onClick={handleFinish}
                  disabled={isSaving || isNavigating}
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
                >
                  <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                  {isSaving ? <ButtonSpinner className="w-5 h-5 ml-2" /> : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )
            )}
          </div>


          {/* Section Title */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{padNav.displayNumber}. {currentSectionTitle}</h2>
          </div>

          {/* Subsections */}
          {Object.entries(subsections).map(([subsectionId, subsection]) => {
            if (!subsection) return null;

            return (
              <div key={subsectionId} className="p-8 mb-8 bg-gray-50 rounded-2xl border border-gray-200">
                <div className="mb-6">
                  <h3 className="text-left text-slate-700 text-xl font-semibold">
                    {rewriteSubsectionTitleDisplay(subsection.subsectionTitleDisplay, padNav.displayNumber)}
                  </h3>
                  {subsectionId === '2' && mdtPlanStrategies.treatmentPlanningNonPharmacological === true && (
                    <span className="mt-1 block w-fit text-[10px] text-violet-600 bg-violet-50 border border-violet-100 rounded px-1.5 py-px font-medium leading-tight">
                      Added to plan in MDT
                    </span>
                  )}
                </div>

                <div className="space-y-4">
                  {/* When an organisation has no configured options for a subsection (e.g.
                      The Owl Centre has no standard non-pharm treatments), show a guidance
                      message directing the clinician to capture details in the free-text
                      preferences section instead. */}
                  {Object.keys(subsection.questions || {}).length === 0 && (
                    <p className="text-center text-gray-500 italic py-4">
                      Your administrator has not set any non-pharmacological options as standard.
                      <br /><br />
                      Please record details of any non-pharmacological options discussed below in the patient's preferences for treatment.
                    </p>
                  )}
                  {Object.entries(subsection.questions || {}).map(([questionId, question]) => {
                    if (!question) return null;

                    // When treatment-first (SLaM), the health-checks gate question
                    // ("Carry out health checks now?") is relocated to the Strategies
                    // page (2_DIS) as a synthetic panel. Hide it here to avoid
                    // showing the same question on both pages.
                    if (
                      isPadTreatmentFirst &&
                      subsectionId === HEALTH_CHECKS_GATE_PATH.subsectionId &&
                      questionId === HEALTH_CHECKS_GATE_PATH.questionId
                    ) {
                      return null;
                    }

                    // Check if this question should be shown based on conditions
                    const shouldShow = checkConditionsToShow(question.conditionsToShow);

                    return (
                      <React.Fragment key={questionId}>
                        {shouldShow && (
                          <div className="mb-4 flex items-start">
                            <div className="w-96 flex flex-col items-start">
                              <div className="flex items-center gap-2">
                                {question.tooltipText ? (
                                  <>
                                    <QuestionMarkCircleIcon
                                      className="w-4 h-4 text-violet-700 cursor-help"
                                      data-tooltip-id={`${question.firestoreFieldName}-tooltip`}
                                      data-tooltip-content={question.tooltipText}
                                    />
                                    <Tooltip
                                      id={`${question.firestoreFieldName}-tooltip`}
                                      place="top"
                                      style={{
                                        maxWidth: '300px',
                                        whiteSpace: 'normal',
                                        backgroundColor: 'rgba(34, 34, 34, 1)',
                                        borderRadius: '15px',
                                        color: '#fff',
                                        fontSize: '0.8em'
                                      }}
                                    />
                                  </>
                                ) : (
                                  <div className="w-4 h-4"></div>
                                )}
                                <h4 className="text-left font-semibold text-gray-700">
                                  {question.questionTitle}
                                </h4>
                              </div>
                              {mdtPlanTreatments[question.firestoreFieldName] === true && (
                                <span className="ml-6 mt-0.5 block w-fit text-[10px] text-violet-600 bg-violet-50 border border-violet-100 rounded px-1.5 py-px font-medium leading-tight">
                                  Added to plan in MDT
                                </span>
                              )}
                            </div>

                            <div className="ml-5">
                              {subsection.subsectionType === 'horizontalOptions' && (
                                <ul className="items-center flex flex-row gap-4">
                                  {(question.options || []).map((option) => {
                                    const currentSelections = formState[question.firestoreFieldName]?.value || [];
                                    const isSelected = currentSelections.includes(option);

                                    return (
                                      <li
                                        key={option}
                                        onClick={() => canEdit && handleHorizontalSelect(
                                          subsectionId,
                                          question.firestoreFieldName,
                                          option,
                                          isSelected
                                        )}
                                        className={`
                                          flex-1 p-2 w-64
                                          rounded-lg transition-all duration-300 ease-in-out
                                          text-center
                                          flex flex-row items-center justify-center gap-2
                                          ${!canEdit
                                            ? 'cursor-not-allowed opacity-75'
                                            : 'cursor-pointer group'
                                          }
                                          ${isSelected
                                            ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                                            : canEdit
                                              ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                                              : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                                          }
                                        `}
                                      >
                                        <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                                          {isSelected ? (
                                            <CheckIcon className="w-4 h-4" />
                                          ) : (
                                            <PlusIcon className="w-4 h-4" />
                                          )}
                                        </div>
                                        <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                                          {option}
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}

                              {subsection.subsectionType === 'verticalOptions' && (
                                <div className="w-96 mt-2">
                                  <SelectionOptions
                                    question={question}
                                    formState={formState}
                                    handleSelect={handleSelect}
                                    handleOtherTextChange={handleOtherTextChange}
                                    handleOtherTextBlur={handleOtherTextBlur}
                                    canEdit={canEdit}
                                    highlightIncompleteOther={
                                      isPadTreatmentFirst &&
                                      question.firestoreFieldName === 'patientTreatmentPreferences' &&
                                      Boolean(treatmentPrefsBlockReason)
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Navigation Buttons */}
          <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">
            {!padNav.showFinish ? (
              <div>
                <button
                  onClick={handleNextSection}
                  disabled={isNextToStrategiesDisabled}
                  data-tooltip-id="treatment-prefs-tooltip-bottom"
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ease-in-out ml-auto ${
                    isNextToStrategiesDisabled
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
                >
                  <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                  {isNavigating
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
                {showTreatmentPrefsGateTooltip && (
                  <Tooltip
                    id="treatment-prefs-tooltip-bottom"
                    place="top"
                    className="z-50 text-xs font-semibold"
                    style={{
                      maxWidth: '250px',
                      whiteSpace: 'normal',
                      backgroundColor: 'rgba(34, 34, 34, 1)',
                      borderRadius: '15px',
                      color: '#fff',
                    }}
                  >
                    {treatmentPrefsBlockReason}
                  </Tooltip>
                )}
              </div>
            ) : (
              canEdit && (
                <button
                  onClick={handleFinish}
                  disabled={isSaving || isNavigating}
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
                >
                  <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                  {isSaving ? <ButtonSpinner className="w-5 h-5 ml-2" /> : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )
            )}
          </div>
          
        </div>
        <ModuleCompletionDialog
          open={isDialogOpen && !isGenerating}
          onOpenChange={setIsDialogOpen}
          title="Post-Assessment Discussion Complete"
          width={600}
          actions={
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              <GenerateReportButton onClick={handleGenerateReport} />
            </>
          }
        >
          <SummarySection title="Summary:">
            The Post-Assessment Discussion has been completed. Would you like to generate the report?
          </SummarySection>
        </ModuleCompletionDialog>

        <GenerateReportProgressDialog
          reportStage="Post-Assessment Discussion"
          patientId={patientId}
          clinicianId={auth.currentUser?.uid || ''}
          handleNextPageButtonClick={handleNextPageButtonClick}
          isGenerating={isGenerating}
          onCancel={handleCancelGeneration}
        />
      </div>
    </Layout>
  );
}